// Centralised date/time display for the admin dashboard.
//
// Backend timestamps are stored and served in UTC (see app/adapters/api/admin.py
// _iso_utc — the ISO strings now carry an explicit +00:00 offset). The dashboard
// renders them in a single fixed business timezone so the client always reads
// local Swiss time regardless of where the browser runs.

export const DISPLAY_TIMEZONE = 'Europe/Zurich';
const LOCALE = 'fr-CH';

/**
 * Short label for the active display zone (e.g. "UTC+2" / "GMT+1"), derived so
 * it stays correct across DST. Falls back to the IANA name if unavailable.
 */
export function displayTimezoneLabel(): string {
  try {
    const parts = new Intl.DateTimeFormat(LOCALE, {
      timeZone: DISPLAY_TIMEZONE,
      timeZoneName: 'short',
    }).formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? DISPLAY_TIMEZONE;
  } catch {
    return DISPLAY_TIMEZONE;
  }
}

/** Format a backend ISO timestamp (UTC) as a full date+time in DISPLAY_TIMEZONE. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '--';
  return new Date(iso).toLocaleString(LOCALE, {
    timeZone: DISPLAY_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/** Format a Date/ISO as time-of-day only, in DISPLAY_TIMEZONE. */
export function formatTime(value: Date | string): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  return d.toLocaleTimeString(LOCALE, {
    timeZone: DISPLAY_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}
